import { useState } from "react"
import { Undo2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { formatDateTime, formatMoney } from "@/lib/formatters"
import { postInline, toastInlineUpdateError, toastInlineUpdateSuccess } from "@/lib/inline-update"
import { cn } from "@/lib/utils"
import {
  REFUND_PATHS,
  refundAmountParam,
  refundBlockedKey,
  refundDisplayAmount,
  refundStatusLabel,
  tRefund,
  type IssuedRefund,
  type RefundEligibility,
  type RefundReasonOption,
} from "@/features/staff/refunds"

/**
 * Giving money back, from the row where it was taken.
 *
 * Three things make this a ceremony rather than an inline update, and each is a
 * rule the design system already states — this action just happens to hit all
 * three at once. It is not undoable (a refund is reversed by charging a diner
 * who is by then holding the money). It is destructive in the literal sense: on
 * the drawer rail a person physically hands over notes. And it needs one piece
 * of information only a human has, the reason.
 *
 * **The reason is required and comes from a closed list.** No free text, ever.
 * The whole value of recording a reason is that someone can count them at the
 * end of the month and know whether to talk to the kitchen or to the floor, and
 * you cannot count "error", "prueba" and "ver con Juan". `Location::RefundPolicy`
 * owns the catalog; this select renders whatever it sends and nothing else.
 *
 * **The amount is the server's.** `refundableCents` already accounts for what
 * came back before, and it is posted back verbatim — the figure a manager
 * confirms, the figure the ledger writes and the figure that seeds the
 * idempotency key are one number. Two implementations of one sum drift the
 * first time a rounding rule changes, and here the drift would be a second
 * refund rather than a rounding error.
 */

type RefundActionProps = {
  paymentAttemptId: number
  eligibility: RefundEligibility
  issued: IssuedRefund[]
  currency: string
  reasons: RefundReasonOption[]
  /** The location's IANA zone. A refund is timestamped where the restaurant is. */
  timeZone?: string
}

export function RefundAction({
  paymentAttemptId,
  eligibility,
  issued,
  currency,
  reasons,
  timeZone,
}: RefundActionProps) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState("")
  const [submitting, setSubmitting] = useState(false)

  const drawerRail = eligibility.rail === "drawer"
  const amount = formatMoney(refundDisplayAmount(eligibility.refundableCents), { currency })

  const handleOpenChange = (next: boolean) => {
    // Escape and the overlay both route through here. A dialog that vanishes
    // mid-request leaves the cashier with no idea whether the money left.
    if (submitting) return

    setOpen(next)
    if (!next) setReason("")
  }

  const handleConfirm = () => {
    if (!reason || submitting) return

    setSubmitting(true)
    postInline(
      REFUND_PATHS.create(paymentAttemptId),
      { reason, amount: refundAmountParam(eligibility.refundableCents) },
      // `preserveState` keeps the dialog mounted through the response, so a
      // refusal the server reports as a flash alert — a drawer refund with no
      // open till, most of all — lands while the manager is still looking at
      // what they chose.
      { preserveState: true }
    )
      .then((result) => {
        setOpen(false)
        setReason("")
        toastInlineUpdateSuccess(result)
      })
      .catch((error: unknown) => toastInlineUpdateError(error))
      .finally(() => setSubmitting(false))
  }

  return (
    <div className="space-y-1.5" data-payment-id={paymentAttemptId}>
      <IssuedRefunds issued={issued} currency={currency} timeZone={timeZone} />

      {eligibility.allowed ? (
        <>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="gap-1.5"
            onClick={() => handleOpenChange(true)}
            data-testid="refund-open"
            data-payment-id={paymentAttemptId}
          >
            <Undo2 className="size-3.5" />
            {tRefund("open")}
          </Button>

          <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent data-testid="refund-dialog">
              <DialogHeader>
                <DialogTitle>{tRefund("title")}</DialogTitle>
                <DialogDescription>{tRefund("description")}</DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="flex items-baseline justify-between gap-4 rounded-md border px-3 py-2">
                  <span className="text-sm text-muted-foreground">{tRefund("amount_label")}</span>
                  <span
                    className="text-sm font-semibold tabular-nums text-foreground"
                    data-testid="refund-amount"
                  >
                    {amount}
                  </span>
                </div>

                {/* The rail is the difference between asking Stripe for something
                    and opening a till. It gets its own block, not a footnote. */}
                {drawerRail ? (
                  <p
                    className="rounded-md border border-warning/40 bg-warning/10 p-3 text-sm"
                    data-testid="refund-drawer-notice"
                    data-payment-id={paymentAttemptId}
                  >
                    {tRefund("drawer_notice", { amount })}
                  </p>
                ) : (
                  <p
                    className="rounded-md border p-3 text-sm text-muted-foreground"
                    data-testid="refund-provider-notice"
                  >
                    {tRefund("provider_notice")}
                  </p>
                )}

                <div className="space-y-2">
                  <Label htmlFor="refund-reason">{tRefund("reason_label")}</Label>
                  <Select value={reason} onValueChange={setReason}>
                    <SelectTrigger
                      id="refund-reason"
                      className="w-full"
                      aria-describedby="refund-reason-hint"
                      data-testid="refund-reason"
                    >
                      <SelectValue placeholder={tRefund("reason_placeholder")} />
                    </SelectTrigger>
                    <SelectContent>
                      {reasons.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          data-testid={`refund-reason-option-${option.value}`}
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p id="refund-reason-hint" className="text-xs text-muted-foreground">
                    {tRefund("reason_hint")}
                  </p>
                </div>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  disabled={submitting}
                  onClick={() => handleOpenChange(false)}
                  data-testid="refund-cancel"
                >
                  {tRefund("cancel")}
                </Button>
                {/* `--destructive-foreground` is declared nowhere in the theme,
                    so destructive surfaces carry literal white, as the kit does. */}
                <Button
                  type="button"
                  variant="destructive"
                  className="text-white"
                  disabled={!reason || submitting}
                  onClick={handleConfirm}
                  data-testid="refund-confirm"
                >
                  {submitting ? tRefund("submitting") : tRefund("confirm")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      ) : (
        <BlockedNotice eligibility={eligibility} paymentAttemptId={paymentAttemptId} />
      )}
    </div>
  )
}

/**
 * Why there is no button.
 *
 * A refused refund says so out loud rather than leaving a blank space, and the
 * drawer case gets the testid of a notice rather than of an error, because
 * "abre la caja" is an instruction, not a failure.
 */
function BlockedNotice({
  eligibility,
  paymentAttemptId,
}: {
  eligibility: RefundEligibility
  paymentAttemptId: number
}) {
  const key = refundBlockedKey(eligibility.reasonCode)
  const drawerBlocked = key === "drawer_no_shift"

  return (
    <p
      className={cn(
        "text-xs",
        drawerBlocked
          ? "rounded-md border border-warning/40 bg-warning/10 p-2.5 text-foreground"
          : "text-muted-foreground"
      )}
      data-testid={drawerBlocked ? "refund-drawer-notice" : "refund-blocked"}
      data-payment-id={paymentAttemptId}
    >
      {tRefund(key)}
    </p>
  )
}

/**
 * What already went back, and who sent it.
 *
 * Failed refunds stay on the list, with what the provider said. A reversal
 * Stripe rejected is the row that explains why a diner is still calling, and
 * hiding it would make the retry look like a duplicate.
 *
 * Amounts render exactly as the server sent them — unsigned. The status word
 * says the money went out; a minus sign invented here would be the browser
 * describing direction, which is the mistake the till screen already refuses to
 * make.
 */
function IssuedRefunds({
  issued,
  currency,
  timeZone,
}: {
  issued: IssuedRefund[]
  currency: string
  timeZone?: string
}) {
  if (issued.length === 0) return null

  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-muted-foreground">{tRefund("issued_title")}</p>
      <ul className="space-y-1">
        {issued.map((refund, index) => (
          <li
            key={refund.id}
            className="flex items-baseline justify-between gap-2 text-xs"
            data-testid={`refund-issued-${index}`}
          >
            <span className="flex min-w-0 items-center gap-1.5">
              {/* The dot rule: colour lives in the mark, and the word beside it
                  carries the same meaning without colour vision. */}
              <span
                aria-hidden="true"
                className={cn(
                  "size-2 shrink-0 rounded-full",
                  refund.status === "succeeded" && "bg-success",
                  refund.status === "pending" && "bg-warning",
                  refund.status === "failed" && "bg-destructive"
                )}
              />
              <span className="min-w-0 truncate text-muted-foreground">
                {refundStatusLabel(refund.status)}
                {" · "}
                {refund.reasonLabel}
                {" · "}
                {refund.actorName ?? tRefund("unknown_actor")}
                {" · "}
                {formatDateTime(refund.createdAt, { timeZone })}
                {refund.failureReason ? ` · ${refund.failureReason}` : ""}
              </span>
            </span>
            <span className="shrink-0 font-medium tabular-nums text-foreground">
              {formatMoney(refund.amount, { currency })}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
